import { isBefore } from "date-fns";
import { action, computed, flow, observable } from "mobx";
import { observer, Observer } from "mobx-react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import React, { useContext, useLayoutEffect, useMemo } from "react";
import { QueryClient, useQueryClient } from "react-query";
import styled from "styled-components";
import { getPanelScrollHeight } from "../../../../Components/getPanelScrollHeight";
import { useCustomAlignDropdownOverlay } from "../../../../Components/useCustomAlignOverlay";
import { getLocStore, useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import {
  GenericDialogActions,
  invalidFieldsCannotBeSavedDialog,
  unsavedChangesDialog,
} from "../../../../WSUtils/genericDialogs";
import { scrollIntoView } from "../../../../WSUtils/scrollIntoView";
import { addUnsavedChange } from "../../../../WSUtils/unsavedChanges";
import { redirect } from "../../../../WSUtils/urlUtils";
import { useLatestQuery } from "../../../../WSUtils/useLatestQuery";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { concatClassnames } from "../../../../WSUtils/util";
import { WSFormGrid } from "../../../../WSUtils/WSFormGrid";
import {
  CVPSearchParams,
  queryStringStore,
} from "../../../../WSUtils/wsQueryStringStore";
import { sentryAssertData } from "../../../../WSUtils/wsSentry";
import { CVEditStepType } from "../../../Steps/Step";
import { CVEditContext } from "../../CVEditContext";
import { CVEditStore, CVEditStoreParams } from "../../CVEditStore";
import {
  CVEditBaseField,
  cveField,
  cveFieldStore,
  OnRenderCvEditComponent,
} from "../../Fields/CVEditBaseField";
import { CVEditCourse } from "./CVEditCourse";
import { saveCurrentlyEditingCourseAndCloseEditor } from "./CVEditCourseSegment";
import { CVEditCourseSegmentStore } from "./CVEditCourseSegmentStore";

export interface CVEditCourseEditorProps {
  courseSegmentStore: CVEditCourseSegmentStore;
}

export const CVEditCourseEditor = ({
  courseSegmentStore,
}: CVEditCourseEditorProps) => {
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);
  const queryClient = useQueryClient();
  const L = useLocStore(LocContext.CV);

  const savingUsingCloseButton = useMemo(() => observable.box(false), []);

  useLayoutEffect(() => {
    scrollIntoView(document.querySelector(".cve-course-editor"));
  }, [courseSegmentStore.editingCourseId]);

  return (
    <Observer
      render={() => {
        const courseEditorStore = courseSegmentStore.courseEditorStore;

        if (!courseEditorStore) return null;

        return (
          <CVEditCourseEditorStyles
            margin="1.15rem"
            float
            className="cve-course-editor cve-x-editor"
          >
            <div className="cvp-h3 cve-smaller-header">
              {!!courseEditorStore.cveCourse.isNew &&
                L.id("e35e5c96f5cc").loc("Nytt kurs")}
              {!courseEditorStore.cveCourse.isNew &&
                L.id("66814f3e60d1").loc("Rediger kurs")}
              <Button
                icon="pi pi-times"
                className={concatClassnames([
                  "p-button-text p-button-icon-only p-button-rounded",
                  "cve-editor-cancel",
                ])}
                loading={savingUsingCloseButton.get()}
                onClick={flow(function* (evt) {
                  evt.preventDefault();

                  onCveCourseEditorCloseButtonClick({
                    courseSegmentStore,
                    courseEditorStore: courseEditorStore,
                    queryClient,
                    onSaveApiCallStart: () => {
                      savingUsingCloseButton.set(true);
                    },
                    onSaveApiCallEnd: () => {
                      savingUsingCloseButton.set(false);
                    },
                  });

                  return false;
                })}
              />
            </div>

            <WSFormGrid horizontal>
              <CVEditBaseField
                def={courseEditorStore.fields.fromDateField}
                containerProps={{ style: { flexGrow: 1 } }}
              />
              <CVEditBaseField
                def={courseEditorStore.fields.toDateField}
                containerProps={{ style: { flexGrow: 1 } }}
              />
            </WSFormGrid>

            <CVEditBaseField def={courseEditorStore.fields.courseTypeField} />
            <CVEditBaseField def={courseEditorStore.fields.organizerField} />
            <CVEditBaseField def={courseEditorStore.fields.descriptionField} />
          </CVEditCourseEditorStyles>
        );
      }}
    />
  );
};

const CVEditCourseEditorStyles = styled(WSFormGrid)`
  &.cve-course-editor {
    .cve-course-expected-finished {
      color: var(--text-color-secondary);
    }
  }
`;

export interface OnCveCourseEditorCloseButtonClickParams {
  courseEditorStore: CVEditCourseEditorStore;
  queryClient: QueryClient;
  onSaveApiCallStart: () => any;
  onSaveApiCallEnd: () => any;
  courseSegmentStore: CVEditCourseSegmentStore;
}

export const onCveCourseEditorCloseButtonClick = flow(function* ({
  courseEditorStore,
  queryClient,
  onSaveApiCallStart,
  onSaveApiCallEnd,
  courseSegmentStore,
}: OnCveCourseEditorCloseButtonClickParams) {
  const userInfo = courseEditorStore.userInfo;
  const cvEditContext = courseEditorStore.cvEditContext;

  const noChanges = courseEditorStore.unsavedChanges.length === 0;
  const noErrors = courseEditorStore.validationErrors.length === 0;
  const isNew = courseEditorStore.cveCourse.isNew;

  if (noChanges && (noErrors || isNew)) {
    courseEditorStore.closeEditor();
    return;
  }

  let hasChanges = !noChanges;
  let hasErrors = !noErrors;

  const L = getLocStore(userInfo, LocContext.CV);

  let action;
  if (hasChanges) {
    action = yield unsavedChangesDialog(
      userInfo,
      L.id("715ce950b70f").loc("Lagre endret kurs?")
    );
  } else if (hasErrors) {
    // this can happen if we change validation requirements
    action = yield invalidFieldsCannotBeSavedDialog(userInfo);
  }

  if (action === GenericDialogActions.SAVE_CHANGES) {
    try {
      onSaveApiCallStart();
      yield saveCurrentlyEditingCourseAndCloseEditor({
        userInfo,
        cvEditContext,
        queryClient,
        courseSegmentStore,
      });
    } finally {
      onSaveApiCallEnd();
    }
  } else if (action === GenericDialogActions.DISCARD_CHANGES) {
    courseEditorStore.closeEditor();
  }

  courseEditorStore.hasAttemptedToClose = true;
});

export interface CVEditCourseEditorStoreParams extends CVEditStoreParams {
  cveCourse: CVEditCourse;
}

export class CVEditCourseEditorStore extends CVEditStore {
  constructor(params: CVEditCourseEditorStoreParams) {
    super(params);

    const needsConfirmationBeforeLeaving = () => {
      const isViewingThis =
        queryStringStore.qs[CVPSearchParams.cvpec] === this.cveCourse.id &&
        this.cvEditContext.currentStep.value === CVEditStepType.Qualifications;

      if (!isViewingThis) return false;
      if (this.isTerminated) return false;
      return this.unsavedChanges.length > 0;
    };

    this.dt.disposers.push(
      addUnsavedChange({
        needsConfirmationBeforeUnload: needsConfirmationBeforeLeaving,
        needsConfirmationBeforeSpaRedirect: needsConfirmationBeforeLeaving,
      })
    );
  }

  @computed get L() {
    return getLocStore(this.userInfo, LocContext.CV);
  }

  cveCourse: CVEditCourse;

  @action closeEditor = () => {
    this.isTerminated = true;
    redirect(null, {
      userInfo: this.userInfo,
      mergeQs: { [CVPSearchParams.cvpec]: null },
    });
  };

  @observable isTerminated = false;

  @observable hasAttemptedToClose = false;

  @computed get fields() {
    return {
      descriptionField: this.descriptionField,
      fromDateField: this.fromDateField,
      toDateField: this.toDateField,
      courseTypeField: this.courseTypeField,
      organizerField: this.organizerField,
    };
  }

  @computed get validationErrors() {
    return Object.values(this.fields).filter((f) => f.store.validationError);
  }

  @computed get visibleValidationErrors() {
    return Object.values(this.fields).filter(
      (f) => f.store.visibleValidationError
    );
  }

  @computed get unsavedChanges(): [string, any, any][] {
    const result = Object.values(this.fields)
      .map((f) => {
        const result = f.hasUnsavedChanges();

        if (!result) return false;

        const [name, val1, val2] = result;

        if (val1 === val2) return false;

        return [name, val1, val2];
      })
      .filter((f) => f);

    return result as any;
  }

  @observable courseTypeField = cveField(() => {
    const _this = this;
    const userInfo = this.userInfo;

    const store = cveFieldStore({
      value: this.cveCourse.courseTypeId,
      get validationError(): string {
        if (!store.value) {
          return _this.L.id("bce9d048492d").loc("Dette feltet er påkrevd");
        }

        return "";
      },
      get showValidationError(): boolean {
        return store.onChangeCount > 0 || _this.hasAttemptedToClose;
      },
    });

    return {
      store,
      id: "CourseType",
      getLabel: () => this.L.id("24737119de92").loc("Kurs"),
      hasUnsavedChanges: () => [
        "CourseType",
        this.cveCourse.courseTypeId,
        store.value,
      ],
      onRenderComponent: observer<OnRenderCvEditComponent>(({ def }) => {
        const { data: educationTypes } = useLatestQuery(
          ["typesAPI.getTypes(kurs)"],
          () =>
            userInfo.fullAPI.typesAPI.getTypes({
              userInfo,
              group: "kurs",
            })
        );

        sentryAssertData(educationTypes, { userInfo });

        const ref = useCustomAlignDropdownOverlay();

        return (
          <Dropdown
            ref={ref}
            className={concatClassnames([
              store.visibleValidationError && "p-invalid",
            ])}
            inputId={def.id}
            name={def.id}
            value={store.value}
            scrollHeight={getPanelScrollHeight()}
            filter
            options={educationTypes?.mapped}
            onChange={action((evt) => {
              let val = evt.value;

              if (val && typeof val === "object") {
                store.value = val.value;
              } else {
                store.value = val;
              }

              store.onChangeCount++;
            })}
          />
        );
      }),
    };
  });

  @observable organizerField = cveField(() => {
    const _this = this;
    const store = cveFieldStore({
      value: this.cveCourse.arranger || "",
      get validationError(): string {
        if (!store.value) {
          return _this.L.id("1727002069f5").loc("Dette feltet er påkrevd");
        }

        if (store.value.length > 100) {
          return _this.L.id("ffb8d2f3cfdd").loc("Maks antall tegn er 100");
        }

        return "";
      },
      get showValidationError(): boolean {
        return (
          store.onChangeCount > 0 ||
          _this.hasAttemptedToClose ||
          store.value.length > 100
        );
      },
    });

    return {
      store,
      id: "Organizer",
      getLabel: () => this.L.id("66a198ffe5c5").loc("Arrangør"),
      hasUnsavedChanges: () => [
        "Organizer",
        this.cveCourse.arranger,
        store.value,
      ],
      onRenderComponent: ({ def }) => (
        <Observer
          render={() => {
            return (
              <InputText
                className={concatClassnames([
                  store.visibleValidationError && "p-invalid",
                ])}
                name={def.id}
                id={def.id}
                value={store.value}
                onChange={action((evt) => {
                  store.value = evt.target.value;
                })}
              />
            );
          }}
        />
      ),
    };
  });

  @observable descriptionField = cveField(() => {
    const _this = this;
    const store = cveFieldStore({
      value: this.cveCourse.description || "",
      get validationError(): string {
        if (store.value.length > 1010) {
          return _this.L.id("0564786cbb9e").loc("Maks antall tegn er 1010");
        }

        return "";
      },
      get showValidationError(): boolean {
        return (
          store.onChangeCount > 0 ||
          _this.hasAttemptedToClose ||
          store.value.length > 1010
        );
      },
    });

    return {
      store,
      id: "Description",
      getLabel: () => this.L.id("53da86d70646").loc("Beskrivelse"),
      hasUnsavedChanges: () => [
        "Description",
        this.cveCourse.description,
        store.value,
      ],
      onRenderComponent: ({ def }) => (
        <Observer
          render={() => {
            return (
              <InputTextarea
                className={concatClassnames([
                  store.visibleValidationError && "p-invalid",
                ])}
                name={def.id}
                id={def.id}
                value={store.value}
                onChange={action((evt) => {
                  store.value = evt.target.value;
                })}
                rows={4}
              />
            );
          }}
        />
      ),
    };
  });

  @observable fromDateField = cveField(() => {
    const _this = this;

    const store = cveFieldStore({
      value: this.cveCourse.from || null,
      get validationError(): string {
        if (!store.value)
          return _this.L.id("60ae7594d613").loc("Dette feltet er påkrevd");

        return "";
      },
      get showValidationError(): boolean {
        return store.onChangeCount > 0 || _this.hasAttemptedToClose;
      },
    });

    return {
      store,
      id: "From",
      getLabel: () => this.L.id("a8f2c9311995").loc("Fra"),
      hasUnsavedChanges: () => [
        "From",
        this.cveCourse.from?.getTime(),
        store.value?.getTime(),
      ],
      onRenderComponent: (args) => (
        <Observer
          render={() => {
            return (
              <Calendar
                className={concatClassnames([
                  store.visibleValidationError && "p-invalid",
                ])}
                inputId={args.def.id}
                name={args.def.id}
                value={store.value}
                view="month"
                dateFormat="mm.yy"
                onChange={(evt) => {
                  store.value = evt.value as Date;
                  store.onChangeCount++;
                }}
              />
            );
          }}
        />
      ),
    };
  });

  @observable toDateField = cveField(() => {
    const _this = this;

    const store = cveFieldStore({
      value: this.cveCourse.to || null,
      get validationError(): string {
        const from = _this.fromDateField.store.value;
        const to = store.value;

        if (!store.value)
          return _this.L.id("e8ccd5eb2a07").loc("Dette feltet er påkrevd");

        if (from && to && isBefore(to, from)) {
          return this.L.id("44c713a67cf5").loc("Tildato må være etter fradato");
        }

        return "";
      },
      get showValidationError(): boolean {
        return store.onChangeCount > 0 || _this.hasAttemptedToClose;
      },
    });

    return {
      store,
      id: "To",
      getLabel: () =>
        this.L.id("57185dc6913f").split(
          "Til {(eller forventet ferdig)}",
          (s) => <span className="cve-course-expected-finished">{s}</span>
        ),
      hasUnsavedChanges: () => [
        "To",
        this.cveCourse.to?.getTime(),
        store.value?.getTime(),
      ],
      onRenderComponent: (args) => (
        <Observer
          render={() => {
            return (
              <Calendar
                className={concatClassnames([
                  store.visibleValidationError && "p-invalid",
                ])}
                inputId={args.def.id}
                name={args.def.id}
                value={store.value}
                view="month"
                dateFormat="mm.yy"
                onChange={(evt) => {
                  store.value = evt.value as Date;
                  store.onChangeCount++;
                }}
              />
            );
          }}
        />
      ),
    };
  });
}
